/**
 * What the client is told about paying a bill: the firm's CURRENT bank details, the same
 * ones the client app and the invoice email show.
 *
 * Read-only on purpose. The details are changed in firm Settings by whoever may change
 * the firm's record (`organization.edit`) -- a billing role able to change the account
 * clients pay into could redirect every payment. When nothing is set, the card says the
 * client is shown nothing, and links to Settings only for someone who can act on it.
 */

import type { FirmPaymentDetails } from '@legalcrm/api';
import { Card, CardBody, CardHeader } from '@legalcrm/ui';
import { Link } from 'react-router-dom';

const LABELS: Array<[keyof FirmPaymentDetails, string]> = [
  ['bank_name', 'Bank'],
  ['account_title', 'Account title'],
  ['account_number', 'Account number'],
  ['iban', 'IBAN'],
  ['branch_code', 'Branch code'],
];

export interface ClientPaymentDetailsCardProps {
  details: FirmPaymentDetails | null;
  /** Whether the reader may change the firm's settings, and so gets a link to them. */
  canEditFirm: boolean;
}

export function ClientPaymentDetailsCard({ details, canEditFirm }: ClientPaymentDetailsCardProps) {
  return (
    <Card sensitivity="client-facing">
      <CardHeader title="How the client pays" />
      <CardBody>
        {details === null ? (
          <p className="text-sm text-ink-subtle">
            No bank details are set, so the client is shown none with this invoice.{' '}
            {canEditFirm ? (
              <Link to="/settings/organization" className="text-primary hover:underline">
                Add them in Settings.
              </Link>
            ) : (
              'An administrator can add them in Settings.'
            )}
          </p>
        ) : (
          <>
            <dl className="flex flex-col gap-2">
              {LABELS.filter(([field]) => details[field]).map(([field, label]) => (
                <div key={field} className="flex items-baseline justify-between gap-3">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                    {label}
                  </dt>
                  <dd className="break-all text-right text-sm text-ink">{details[field]}</dd>
                </div>
              ))}
            </dl>
            {details.instructions && (
              <p className="mt-3 whitespace-pre-line text-sm text-ink-muted">
                {details.instructions}
              </p>
            )}
            <p className="mt-3 text-xs text-ink-subtle">
              The firm&rsquo;s current details, shown to the client with every invoice.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}
